import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BookingService } from '@/services/booking.service';
import {
  FacilityType,
  BookingAvailabilityResponse,
} from '@/types/booking.types';
import { shortCacheQueryOptions } from '@/config/react-query.config';

// Query key for booking availability
export const bookingAvailabilityKeys = {
  all: ['booking-availability'] as const,
  forLocation: (
    locationId: string,
    facility: FacilityType,
    bookingDate: string,
    selectedCoachId?: string,
    selectedBayId?: string
  ) =>
    [
      ...bookingAvailabilityKeys.all,
      locationId,
      facility,
      bookingDate,
      selectedCoachId ?? 'NO_COACH',
      selectedBayId ?? 'NO_BAY',
    ] as const,
};

/**
 * Everything one availability read is identified by.
 *
 * The endpoint answers for ONE `bookingDate` per call — unlike the
 * hitting-bay one, which returns a 7-day window — so the date is
 * part of the cache key and every day is its own request.
 */
export type BookingAvailabilityArgs = {
  locationId: string;
  facility: FacilityType;
  bookingDate: string;
  selectedCoachId?: string;
  timezone?: string;
  rescheduleBookingId?: string;
  selectedBayId?: string;
};

/**
 * The key and the fetcher for one day, in one place.
 *
 * THE PREFETCHER AND THE HOOK MUST BUILD THE SAME KEY. If they
 * drift by so much as the `NO_RESCHEDULE` sentinel, the prefetch
 * still fires and still fills the cache — under a key the screen
 * never reads. The result is not a broken screen but a silently
 * useless one: double the requests, none of them warm. Both call
 * this rather than spelling the key out.
 */
const availabilityQuery = (args: BookingAvailabilityArgs) => ({
  queryKey: [
    ...bookingAvailabilityKeys.forLocation(
      args.locationId,
      args.facility,
      args.bookingDate,
      args.selectedCoachId,
      args.selectedBayId
    ),
    args.rescheduleBookingId ?? 'NO_RESCHEDULE',
  ],
  queryFn: () => {
    if (args.selectedCoachId) {
      return BookingService.getAvailabilityWithCoach(
        args.locationId,
        args.facility,
        args.selectedCoachId,
        args.bookingDate,
        args.timezone,
        args.rescheduleBookingId,
        args.selectedBayId
      );
    }

    return BookingService.getAvailabilityForFacility(
      args.locationId,
      args.facility,
      args.bookingDate,
      args.timezone,
      args.rescheduleBookingId,
      args.selectedBayId
    );
  },
});

/** A day this endpoint can actually answer for. Hitting bay has no
 *  booking-type calendar and is served by its own 7-day endpoint. */
const isFetchable = (args: BookingAvailabilityArgs): boolean =>
  Boolean(args.locationId && args.facility && args.bookingDate) &&
  args.facility !== FacilityType.HITTING_BAY;

/**
 * Warm the cache for a day the member has not asked for yet.
 *
 * Because the endpoint is one-day-per-call, every date tap was a
 * cold cache and a visible spinner. Nothing on the client can make
 * the request unnecessary — only make it have already happened.
 *
 * `prefetchQuery` resolves quietly on failure and never throws, so a
 * dead prefetch costs nothing: the day is simply cold when the
 * member reaches it, exactly as it is today. Passing `staleTime`
 * makes a repeat call for a day already in cache a no-op rather than
 * a second request.
 */
export const usePrefetchBookingAvailability = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (args: BookingAvailabilityArgs): Promise<void> => {
      if (!isFetchable(args)) return Promise.resolve();
      return queryClient.prefetchQuery({
        ...availabilityQuery(args),
        staleTime: shortCacheQueryOptions.staleTime,
      });
    },
    [queryClient]
  );
};

/**
 * Hook to get booking availability with conditional coach/bay filtering
 */
export const useBookingAvailability = (
  locationId: string,
  facility: FacilityType,
  bookingDate: string,
  selectedCoachId?: string,
  timezone?: string,
  rescheduleBookingId?: string,
  selectedBayId?: string
) => {
  const args: BookingAvailabilityArgs = {
    locationId,
    facility,
    bookingDate,
    selectedCoachId,
    timezone,
    rescheduleBookingId,
    selectedBayId,
  };

  const query = useQuery<BookingAvailabilityResponse, Error>({
    ...availabilityQuery(args),
    enabled: isFetchable(args),
    ...shortCacheQueryOptions,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch, // For manual refetch needs (date changes, retry button)
  };
};
